import {AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, LayoutDashboardIcon, Loader2Icon, PinIcon, XIcon} from "lucide-react";
import {Component, type ErrorInfo, type ReactNode, useCallback, useEffect, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {
    deleteSandboxArtifact,
    dismissSandboxInsight,
    registerSandboxDataSources,
    type SandboxArtifact,
    type SandboxDataSource,
    type SandboxInsight,
    type SandboxPromotion,
    toggleSandboxPin,
    useVNextSandboxDataSources,
    useVNextSandboxPromotions,
    useVNextSandboxRunDetail,
    useVNextSandboxRuns,
    useVNextTabArtifacts,
} from "@hooks/use-vnext";

import {GenericReportCard} from "./GenericReportCard";
import {normalizeArtifactReport} from "./normalize-artifact";
import {SandboxInsightCard} from "./SandboxInsightCard";
import {SandboxPromotionDialog} from "./SandboxPromotionDialog";

interface SandboxTabProps {
    workspaceId: string;
    scenarioId: string;
}

class SandboxErrorBoundary extends Component<
    {children: ReactNode},
    {hasError: boolean; error: string}
> {
    state = {hasError: false, error: ""};

    static getDerivedStateFromError(error: Error) {
        return {hasError: true, error: error.message};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("SandboxTab render error:", error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircleIcon className="h-8 w-8 text-red-400 mb-2" />
                    <p className="text-sm font-medium text-red-600">Sandbox tab encountered an error</p>
                    <p className="text-xs text-red-400 mt-1 max-w-md">{this.state.error}</p>
                    <button
                        onClick={() => this.setState({hasError: false, error: ""})}
                        className="mt-3 text-xs text-[#209DB3] hover:underline"
                    >
                        Try again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const SOURCE_TYPE_COLORS: Record<string, string> = {
    xero_live: "bg-blue-100 text-blue-700",
    vnext_snapshot: "bg-purple-100 text-purple-700",
    knowledge_doc: "bg-amber-100 text-amber-700",
    knowledge_dataset: "bg-amber-100 text-amber-700",
    sandbox_upload: "bg-green-100 text-green-700",
    manual_input: "bg-gray-100 text-gray-700",
};

export function SandboxTab(props: SandboxTabProps) {
    return (
        <SandboxErrorBoundary>
            <SandboxTabInner {...props} />
        </SandboxErrorBoundary>
    );
}

function SandboxTabInner({workspaceId}: SandboxTabProps) {
    const {runs, isLoading: runsLoading} = useVNextSandboxRuns(workspaceId);
    const {promotions, refresh: refreshPromos} = useVNextSandboxPromotions(workspaceId);
    const {dataSources, isLoading: sourcesLoading, refresh: refreshSources} =
        useVNextSandboxDataSources(workspaceId);

    // ── Artifacts: fetch ALL sandbox artifacts (not scoped to a single run) ──
    const {
        artifacts: allArtifacts,
        isLoading: artifactsLoading,
        refresh: refreshArtifacts,
    } = useVNextTabArtifacts(workspaceId, "sandbox");

    // ── Latest run: used only for run status + insights ──
    const latestRun = runs.length > 0 ? runs[0] : null;
    const {
        insights,
        refresh: refreshRunDetail,
    } = useVNextSandboxRunDetail(
        workspaceId,
        latestRun?.id ?? null,
    );

    const [promoteInsightId, setPromoteInsightId] = useState<string | null>(null);
    const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());

    const toggleCollapse = useCallback((artifactId: string) => {
        setExpandedArtifacts(prev => {
            const next = new Set(prev);
            if (next.has(artifactId)) next.delete(artifactId);
            else next.add(artifactId);
            return next;
        });
    }, []);

    const handleDeleteArtifact = useCallback(
        async (artifactId: string) => {
            await deleteSandboxArtifact(workspaceId, artifactId);
            refreshArtifacts();
        },
        [workspaceId, refreshArtifacts],
    );

    const handleTogglePin = useCallback(
        async (artifactId: string, currentlyPinned: boolean) => {
            await toggleSandboxPin(workspaceId, artifactId, !currentlyPinned);
            refreshArtifacts();
        },
        [workspaceId, refreshArtifacts],
    );

    // Keep source registry current for artifact source footnotes.
    useEffect(() => {
        if (workspaceId && dataSources.length === 0 && !sourcesLoading) {
            registerSandboxDataSources(workspaceId)
                .then(() => refreshSources())
                .catch(() => {});
        }
    }, [workspaceId, dataSources.length, sourcesLoading, refreshSources]);

    const handleDismiss = useCallback(
        async (insightId: string) => {
            await dismissSandboxInsight(workspaceId, insightId);
            refreshRunDetail();
        },
        [workspaceId, refreshRunDetail],
    );

    const handlePromoted = useCallback(() => {
        setPromoteInsightId(null);
        refreshRunDetail();
        refreshArtifacts();
        refreshPromos();
        window.dispatchEvent(new CustomEvent("vnext-data-updated"));
    }, [refreshRunDetail, refreshArtifacts, refreshPromos]);

    const promoteInsight = promoteInsightId
        ? insights.find((insight: SandboxInsight) => insight.id === promoteInsightId) ?? null
        : null;

    const activeInsights = insights.filter(
        (insight: SandboxInsight) => insight.status === "draft" || insight.status === "reviewed",
    );

    if (runsLoading && artifactsLoading) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-52 w-full" />
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 min-w-0 space-y-6 overflow-y-auto">
            {/* Run status banner */}
            {latestRun && latestRun.status === "running" && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                    Analysis running...
                </div>
            )}

            {/* All Sandbox Artifacts */}
            {allArtifacts.length > 0 ? (
                <div className="space-y-3">
                    {allArtifacts.map((artifact: SandboxArtifact) => {
                        const normalizedReport = normalizeArtifactReport(artifact.report_json);
                        const isCollapsed = !expandedArtifacts.has(artifact.id);
                        return (
                            <SandboxErrorBoundary key={artifact.id}>
                                <div className="relative group rounded-lg border border-[#209DB3]/20 overflow-hidden">
                                    <button
                                        onClick={() => toggleCollapse(artifact.id)}
                                        className="flex w-full items-center gap-2 bg-[#209DB3] px-4 py-2.5 text-left text-white hover:bg-[#1a8a9e] transition-colors"
                                    >
                                        {isCollapsed ? (
                                            <ChevronDownIcon className="h-4 w-4 shrink-0" />
                                        ) : (
                                            <ChevronUpIcon className="h-4 w-4 shrink-0" />
                                        )}
                                        <span className="flex-1 text-sm font-semibold truncate">{artifact.title}</span>
                                        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                            {artifact.created_at && (
                                                <span className="text-[10px] text-white/70 tabular-nums">
                                                    {new Date(artifact.created_at).toLocaleString("en-AU", {day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"})}
                                                </span>
                                            )}
                                            <button
                                                onClick={() => handleTogglePin(artifact.id, artifact.is_pinned)}
                                                className={`transition-colors ${
                                                    artifact.is_pinned
                                                        ? "text-amber-300 hover:text-amber-200"
                                                        : "text-white/40 hover:text-white/80 opacity-0 group-hover:opacity-100"
                                                }`}
                                                title={artifact.is_pinned ? "Unpin" : "Pin"}
                                            >
                                                <PinIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteArtifact(artifact.id)}
                                                className="text-white/40 hover:text-red-300 transition-colors opacity-0 group-hover:opacity-100"
                                                title="Delete artifact"
                                            >
                                                <XIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </button>

                                    {!isCollapsed && (
                                        <div className="p-4">
                                            {normalizedReport ? (
                                                <GenericReportCard report={normalizedReport} />
                                            ) : (
                                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
                                                    <p>Unsupported artifact format: {artifact.title}</p>
                                                    <details className="mt-2">
                                                        <summary className="cursor-pointer text-[11px] text-amber-800">
                                                            Show raw payload
                                                        </summary>
                                                        <pre className="mt-1 max-h-40 overflow-auto rounded border border-amber-200 bg-amber-100/40 p-2 text-[10px] text-amber-900">
                                                            {JSON.stringify(artifact.report_json, null, 2)}
                                                        </pre>
                                                    </details>
                                                </div>
                                            )}

                                            {/* Source footnotes */}
                                            {artifact.source_ids.length > 0 && (
                                                <div className="mt-2 flex flex-wrap items-center gap-1">
                                                    <span className="text-[10px] text-gray-400">Sources:</span>
                                                    {artifact.source_ids.map((sourceId: string) => {
                                                        const source = dataSources.find(
                                                            (dataSource: SandboxDataSource) => dataSource.id === sourceId,
                                                        );

                                                        if (!source) {
                                                            return (
                                                                <Badge key={sourceId} variant="secondary" className="bg-gray-100 text-[9px] text-gray-600">
                                                                    Unknown
                                                                </Badge>
                                                            );
                                                        }

                                                        return (
                                                            <Badge
                                                                key={sourceId}
                                                                variant="secondary"
                                                                className={`text-[9px] py-0 px-1 ${
                                                                    SOURCE_TYPE_COLORS[source.source_type] || "bg-gray-100 text-gray-600"
                                                                }`}
                                                            >
                                                                {source.label}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </SandboxErrorBoundary>
                        );
                    })}
                </div>
            ) : !artifactsLoading && !latestRun ? (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <LayoutDashboardIcon className="mx-auto mb-3 size-8 text-muted-foreground" />
                        <p className="text-lg font-medium">No Sandbox Analysis Yet</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Ask the agent for ad hoc analysis to generate cards, widgets, and charts here.
                        </p>
                    </CardContent>
                </Card>
            ) : null}

            {/* Insights from latest run */}
            {activeInsights.length > 0 && (
                <Card>
                    <CardContent className="space-y-3 p-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">Insights</h3>
                            <span className="text-[10px] text-gray-400">
                                {activeInsights.length} finding
                                {activeInsights.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {activeInsights.map((insight: SandboxInsight) => (
                                <SandboxInsightCard
                                    key={insight.id}
                                    insight={insight}
                                    onPromote={setPromoteInsightId}
                                    onDismiss={handleDismiss}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Promotion History */}
            {promotions.length > 0 && (
                <Card>
                    <CardContent className="space-y-2 p-4">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Promotion History
                        </h3>
                        <div className="space-y-1.5">
                            {promotions.map((promotion: SandboxPromotion) => (
                                <div
                                    key={promotion.id}
                                    className="flex items-center gap-2 text-xs text-gray-600"
                                >
                                    <span className="shrink-0 text-gray-400">
                                        {promotion.promoted_at
                                            ? new Date(promotion.promoted_at).toLocaleDateString()
                                            : ""}
                                    </span>
                                    <span className="truncate">
                                        &ldquo;{promotion.insight_title}&rdquo;
                                    </span>
                                    {promotion.op_label && (
                                        <>
                                            <span className="text-gray-300">&rarr;</span>
                                            <span className="truncate text-gray-500">
                                                {promotion.op_label}
                                            </span>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Promotion Dialog */}
            {promoteInsight && (
                <SandboxPromotionDialog
                    workspaceId={workspaceId}
                    insight={promoteInsight}
                    onClose={() => setPromoteInsightId(null)}
                    onPromoted={handlePromoted}
                />
            )}
        </div>
    );
}
